/**
 * Hotkey Settings - Frontend Types & Defaults
 *
 * Re-exports shared types and defines UI metadata + default bindings
 * that match the current hardcoded hotkeys in each station page.
 */

import type { HotkeyAction, HotkeyBinding, HotkeyConfig } from '@threadheads/shared';
import { HOTKEY_STATION_ACTIONS as SHARED_STATION_ACTIONS } from '@threadheads/shared';

// Re-export shared types for convenience
export type { HotkeyAction, HotkeyBinding, HotkeyConfig };
export { HOTKEY_ACTIONS, HotkeyActionSchema, HotkeyBindingSchema, HotkeyConfigSchema, HOTKEY_STATION_ACTIONS } from '@threadheads/shared';

// ============================================================================
// Station Grouping
// ============================================================================

export type StationGroup = 'catcher' | 'printer' | 'packer';

const STATION_LABELS: Record<StationGroup, string> = {
  catcher: 'Catcher',
  printer: 'Printer',
  packer: 'Packer',
};

/** Station groups with labels, derived from the shared HOTKEY_STATION_ACTIONS source of truth */
export const STATION_GROUPS: Record<StationGroup, { label: string; actions: HotkeyAction[] }> = Object.fromEntries(
  Object.entries(SHARED_STATION_ACTIONS).map(([key, actions]) => [
    key,
    { label: STATION_LABELS[key as StationGroup] ?? key, actions: [...actions] },
  ]),
) as Record<StationGroup, { label: string; actions: HotkeyAction[] }>;

// ============================================================================
// Action Metadata (for settings UI)
// ============================================================================

export interface HotkeyActionMeta {
  label: string;
  description: string;
  station: StationGroup;
}

export const HOTKEY_ACTION_LABELS: Record<HotkeyAction, HotkeyActionMeta> = {
  // Catcher
  catcher_primary: {
    label: 'Primary Action (Accept / Undo)',
    description: 'Accept garment or undo if already caught',
    station: 'catcher',
  },
  catcher_redo: {
    label: 'Redo',
    description: 'Open redo dialog',
    station: 'catcher',
  },
  catcher_back: {
    label: 'Clear',
    description: 'Clear garment and return to scanner',
    station: 'catcher',
  },

  // Printer
  printer_print: {
    label: 'Print (Single)',
    description: 'Confirm print in single-print mode',
    station: 'printer',
  },
  printer_front: {
    label: 'Print Front (Dual)',
    description: 'Print front side in dual-print mode',
    station: 'printer',
  },
  printer_back: {
    label: 'Print Back (Dual)',
    description: 'Print back side in dual-print mode',
    station: 'printer',
  },
  printer_issue: {
    label: 'Report Issue',
    description: 'Open print file issue dialog',
    station: 'printer',
  },
  printer_wrong: {
    label: 'Garment Issue',
    description: 'Open garment issue dialog',
    station: 'printer',
  },
  printer_clear: {
    label: 'Clear',
    description: 'Clear garment and return to scanner',
    station: 'printer',
  },

  // Packer
  packer_ship: {
    label: 'Print Label / Ship',
    description: 'Primary action: Print Shipping Label (StarShipIt) or Mark Complete (manual)',
    station: 'packer',
  },
  packer_manual: {
    label: 'Complete Manually',
    description: 'Complete packing manually',
    station: 'packer',
  },
  packer_copy: {
    label: 'Copy Order',
    description: 'Copy order number to clipboard',
    station: 'packer',
  },
  packer_clear: {
    label: 'Clear Order',
    description: 'Clear current order',
    station: 'packer',
  },
  packer_preview: {
    label: 'View Images',
    description: 'Open the big image view for this order. Arrow keys flick through, same key closes',
    station: 'packer',
  },
  packer_confirm: {
    label: 'Confirm Shipment',
    description: 'Confirm shipment or dismiss auto-ship result',
    station: 'packer',
  },
  packer_label: {
    label: 'Reprint Label',
    description: 'Reprint shipping label after generation (success state only)',
    station: 'packer',
  },
};

// ============================================================================
// Default Hotkey Map (matches current hardcoded bindings)
// ============================================================================

function combo(key: string, ...modifiers: Array<'ctrl' | 'alt' | 'meta' | 'shift'>): HotkeyBinding {
  return { type: 'combo', modifiers: modifiers.length ? modifiers : ['ctrl'], key };
}

/**
 * Default hotkey bindings matching the current hardcoded keys in each station.
 * When the DB returns null (no custom config), these are used.
 */
export const DEFAULT_HOTKEY_MAP: Record<HotkeyAction, HotkeyBinding | null> = {
  // Catcher (from CatcherPage.tsx lines 434-454)
  catcher_primary: combo('Enter'),
  catcher_redo: combo('r'),
  catcher_back: combo('Backspace'),

  // Printer (from PrintStationPage.tsx lines 343-386)
  // Note: Ctrl+P alias dropped — conflicts with browser print dialog
  printer_print: combo('Enter'),
  printer_front: combo('f'),
  printer_back: combo('b'),
  printer_issue: combo('i'),
  printer_wrong: combo('w'),
  printer_clear: combo('Backspace'),

  // Packer (from PackingPage.tsx lines 165-205)
  packer_ship: combo('Enter'),
  packer_manual: combo('m'),
  packer_copy: combo('c'),
  packer_clear: combo('Backspace'),
  // Not Ctrl+Space: that toggles the Chinese IME on Windows, and Thread Lab
  // packs on this station.
  packer_preview: combo('i'),

  // Packer - ShippingPanel (from ShippingPanel.tsx lines 382-410)
  packer_confirm: combo('Enter'),
  packer_label: combo('l'),
};

/**
 * Merge DB hotkey config over defaults.
 * Handles missing keys (new actions added after user saved config)
 * and null values (unbound actions).
 */
export function mergeHotkeyConfig(dbConfig: HotkeyConfig | null | undefined): Record<HotkeyAction, HotkeyBinding | null> {
  if (!dbConfig) return { ...DEFAULT_HOTKEY_MAP };

  const merged = { ...DEFAULT_HOTKEY_MAP };
  for (const [action, binding] of Object.entries(dbConfig)) {
    if (action in merged) {
      merged[action as HotkeyAction] = binding;
    }
  }
  return merged;
}

/** Type alias for the resolved hotkey map used by the engine */
export type HotkeyMap = Record<HotkeyAction, HotkeyBinding | null>;

/**
 * Format a binding as a short display string for station UI badges.
 * Returns e.g. "Ctrl+↵", "Hold R", or "" for unbound.
 */
export function formatBindingLabel(binding: HotkeyBinding | null): string {
  if (!binding) return '';
  if (binding.type === 'combo') {
    const parts: string[] = [];
    if (binding.modifiers.includes('ctrl')) parts.push('Ctrl');
    if (binding.modifiers.includes('alt')) parts.push('Alt');
    if (binding.modifiers.includes('meta')) parts.push('Cmd');
    if (binding.modifiers.includes('shift')) parts.push('Shift');
    let key = binding.key;
    if (key === 'Enter') key = '↵';
    else if (key === 'Backspace') key = '⌫';
    else if (key === ' ') key = 'Space';
    else if (key.length === 1) key = key.toUpperCase();
    parts.push(key);
    return parts.join('+');
  }
  let key = binding.key;
  if (key === 'Enter') key = '↵';
  else if (key === 'Backspace') key = '⌫';
  else if (key.length === 1) key = key.toUpperCase();
  return `Hold ${key}`;
}
